// Datos clínicos comunes de la cabecera de la bitácora UCI (113/114/115): alergias, grupo sanguíneo, factor Rh y días de hospitalización.
//
// Papel: "ALERGIAS:" y "GRUPO Y FACTOR:" son un solo renglón cada uno; el INSTRUCTIVO oficial (pág. 223 del PDF, bloque C) los pide separados:
// "Alergias: si, no, especificar", "Grupo sanguíneo:", "Factor Rh:". Se siguen las tres casillas del instructivo. Un "No" explícito se
// distingue de "sin responder" (mismo criterio que `SiNoValor`). El texto libre antiguo ("Grupo y factor") ya no se captura: lo firmado
// antes conserva su `Par` viejo, y el estado no se rehidrata.
import type { Par } from '../index';
import type { Traducir } from '../tecnicaAnestesicaDatos';
import { leerSiNo, textoSiNo, type SiNoValor } from '../camposSimplesDatos';

export const GRUPOS_SANGUINEOS = ['A', 'B', 'AB', 'O'] as const;
export type GrupoSanguineo = (typeof GRUPOS_SANGUINEOS)[number];

export const FACTORES_RH = ['positivo', 'negativo'] as const;
export type FactorRh = (typeof FACTORES_RH)[number];

export interface EstadoAlergiasSangre {
  readonly alergiasSiNo: SiNoValor;
  /** "Especificar" del instructivo. Solo cuenta si la respuesta es Sí (o si no se contestó Sí/No pero se escribió algo). */
  readonly alergias: string;
  readonly grupoSanguineo: GrupoSanguineo | '';
  readonly factorRh: FactorRh | '';
}

export function estadoInicialAlergiasSangre(): EstadoAlergiasSangre {
  return { alergiasSiNo: '', alergias: '', grupoSanguineo: '', factorRh: '' };
}

/** Texto de alergias para el `Par[]`: "No", "Sí", "Sí: penicilina" o, sin Sí/No pero con texto, el texto. `''` si no hay nada. */
export function textoAlergias(e: Pick<EstadoAlergiasSangre, 'alergiasSiNo' | 'alergias'>, textos?: { readonly si: string; readonly no: string }): string {
  const siNo = leerSiNo(e.alergiasSiNo);
  const detalle = e.alergias.trim();
  if (siNo === 'no') return textoSiNo('no', textos);
  if (siNo === 'si') return detalle === '' ? textoSiNo('si', textos) : `${textoSiNo('si', textos)}: ${detalle}`;
  return detalle;
}

/** Los tres pares (alergias, grupo sanguíneo, factor Rh) en el orden del instructivo. `preguntaAlergias` conserva el rótulo que ya tenía cada formulario. */
export function paresAlergiasSangre(t: Traducir, preguntaAlergias: string, e: EstadoAlergiasSangre): Par[] {
  return [
    { pregunta: preguntaAlergias, respuesta: textoAlergias(e, { si: t('formulario.si'), no: t('formulario.no') }) },
    { pregunta: t('formularios.uci.datosClinicos.grupoSanguineo'), respuesta: e.grupoSanguineo },
    { pregunta: t('formularios.uci.datosClinicos.factorRh'), respuesta: e.factorRh === '' ? '' : t(e.factorRh === 'positivo' ? 'formularios.uci.datosClinicos.rhPositivo' : 'formularios.uci.datosClinicos.rhNegativo') },
  ];
}

const FECHA_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

function diaUtc(texto: string): number | undefined {
  const m = FECHA_ISO.exec(texto.trim());
  if (!m) return undefined;
  const [anio, mes, dia] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const ms = Date.UTC(anio, mes - 1, dia);
  const d = new Date(ms);
  // Descarta fechas imposibles (2026-02-31 se corre a marzo).
  return d.getUTCFullYear() === anio && d.getUTCMonth() === mes - 1 && d.getUTCDate() === dia ? ms / 86_400_000 : undefined;
}

/**
 * Día de UCI = fecha actual − fecha de ingreso + 1: el día del ingreso es el día 1 (el instructivo, pág. 223, dice que el número de hoja
 * "indica cuántos días está el paciente en UCI", y la primera hoja es la del día del ingreso). `''` si falta una fecha, no es válida o la
 * fecha actual es anterior al ingreso. Sin reloj: solo depende de las dos fechas.
 */
export function diasHospitalizacionUci(fechaIngreso: string, fechaActual: string): string {
  const a = diaUtc(fechaIngreso);
  const b = diaUtc(fechaActual);
  if (a === undefined || b === undefined || b < a) return '';
  return String(b - a + 1);
}

/** Lo que se ve e imprime: el cálculo si las fechas lo permiten; si no, lo que se escribió a mano. */
export function diasHospitalizacionEfectivo(fechaIngreso: string, fechaActual: string, manual: string): string {
  return diasHospitalizacionUci(fechaIngreso, fechaActual) || manual.trim();
}
